import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, where, onSnapshot, Timestamp, orderBy } from 'firebase/firestore';

const STATUS = {
  assigned:      { color: '#6b7280', label: 'Assigned', icon: '📋' },
  en_route:      { color: '#3b82f6', label: 'Driver En Route to Customer', icon: '🚗' },
  picked_up:     { color: '#F97316', label: 'Vehicle Picked Up', icon: '🔑' },
  arriving:      { color: '#9333ea', label: 'Arriving at Your Location', icon: '📍' },
  dropped_off:   { color: '#16a34a', label: 'Vehicle Dropped Off', icon: '✅' },
  return_route:  { color: '#3b82f6', label: 'Driver Returning Vehicle', icon: '🔄' },
  completed:     { color: '#6b7280', label: 'Completed', icon: '✅' },
};

export default function DriverCoordinationTab({ user, centerData }) {
  const [jobs, setJobs]       = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!centerData?.id) { setLoading(false); return; }
    const start = new Date(); start.setHours(0,0,0,0);
    const end   = new Date(); end.setHours(23,59,59,999);
    const q = query(
      collection(db, 'washRequests'),
      where('serviceCenterId', '==', centerData.id),
      where('scheduledAt', '>=', Timestamp.fromDate(start)),
      where('scheduledAt', '<=', Timestamp.fromDate(end)),
      orderBy('scheduledAt', 'asc')
    );
    const unsub = onSnapshot(q, snap => {
      setJobs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [centerData]);

  const activeJobs = jobs.filter(j => !['completed'].includes(j.status));
  const completedJobs = jobs.filter(j => j.status === 'completed');

  return (
    <div style={{ padding: 32 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 900 }}>Driver Coordination</h1>
        <p style={{ color: '#6b7280', marginTop: 4 }}>Real-time view of drivers bringing vehicles to your facility today</p>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Active Jobs', value: activeJobs.length, color: '#F97316' },
          { label: 'Vehicles at Facility', value: jobs.filter(j => j.centerStatus === 'in_service').length, color: '#16a34a' },
          { label: 'Completed Today', value: completedJobs.length, color: '#6b7280' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#111827' }}>{s.value}</div>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {loading ? <p style={{ color: '#6b7280' }}>Loading...</p> : jobs.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🚗</div>
          <h3 style={{ fontWeight: 800, marginBottom: 6 }}>No drivers today</h3>
          <p style={{ color: '#6b7280' }}>Driver activity will appear here in real time when jobs are assigned</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {jobs.map(job => {
            const driverStatus = STATUS[job.driverStatus] || STATUS.assigned;
            const time = job.scheduledAt?.toDate?.()?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            return (
              <div key={job.id} style={{ background: '#fff', borderRadius: 16, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `4px solid ${driverStatus.color}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                      <span style={{ fontSize: 20 }}>{driverStatus.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: driverStatus.color }}>{driverStatus.label}</span>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 4 }}>
                      {job.vehicleColor} {job.vehicleMake} {job.vehicleModel}
                      {job.licensePlate ? ` · ${job.licensePlate}` : ''}
                    </div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 4 }}>Services: {(job.selectedServices || []).join(', ') || 'Vehicle Service'}</div>
                    <div style={{ fontSize: 13, color: '#6b7280' }}>Scheduled: {time} · Driver: {job.driverName || 'Assigned'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 16, fontWeight: 900, color: '#111827' }}>${(job.quotedCenterAmount || 0).toFixed(2)}</div>
                    <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>#{job.id.slice(-6).toUpperCase()}</div>
                  </div>
                </div>
                {job.driverPhone && (
                  <div style={{ marginTop: 12, padding: 10, background: '#f9fafb', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, color: '#374151' }}>Driver contact available through CocoWash</span>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>All calls masked for privacy</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}